import React, { useMemo, useCallback } from 'react';
import InfoIcon from '@atlaskit/icon/core/status-information';
import { token } from '@atlaskit/tokens';
import type { MaybeChoreographedComponentProps } from '@atlassian/jira-choreographer-services/src/types.tsx';
import { InfoFlag } from '@atlassian/jira-flags';
import { useHelpPanelLink } from '@atlassian/jira-help-panel-button/src/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, type AnalyticsEvent } from '@atlassian/jira-product-analytics-bridge';
import { CUSTOM_NAV_BAR_COLORS_FLAG_ID, HELP_ARTICLE_ID } from '../common/constants.tsx';
import messages from './messages.tsx';

export const CUSTOM_NAV_BAR_COLORS_FLAG_TEST_ID =
	'router-resources.theme-preference.custom-nav-bar-colors-flag';

type Props = {
	onDismiss: () => void;
	analyticsAttributes: {
		backgroundColor: string;
		highlightColor: string;
	};
} & Partial<MaybeChoreographedComponentProps>;

const CustomNavBarColorsFlag = ({
	onDismiss,
	analyticsAttributes,
	messageId,
	messageType,
}: Props): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const onOpenInProductHelpArticle = useHelpPanelLink();

	const onDismissFn = useCallback(
		(
			_: React.MouseEvent<HTMLElement, MouseEvent> | string | number,
			analyticsEvent: AnalyticsEvent,
		) => {
			fireUIAnalytics(
				analyticsEvent,
				'customTopNavUserFlagWarningDismissButton',
				analyticsAttributes,
			);
			onDismiss();
		},
		[onDismiss, analyticsAttributes],
	);

	return useMemo(
		() => (
			<InfoFlag
				icon={<InfoIcon spacing="spacious" color={token('color.icon.information')} label="" />}
				testId={CUSTOM_NAV_BAR_COLORS_FLAG_TEST_ID}
				id={CUSTOM_NAV_BAR_COLORS_FLAG_ID}
				title={formatMessage(messages.title)}
				description={formatMessage(messages.description)}
				onDismissed={onDismissFn}
				messageId={messageId ?? 'custom-nav-bar-colors-flag.ui.info-flag'}
				messageType={messageType ?? 'transactional'}
				actions={[
					{
						content: formatMessage(messages.learnMoreAction),
						onClick: (_, analyticsEvent: AnalyticsEvent) => {
							fireUIAnalytics(
								analyticsEvent,
								'customTopNavUserFlagWarningMoreButton',
								analyticsAttributes,
							);
							onOpenInProductHelpArticle(HELP_ARTICLE_ID);
						},
					},
					{
						content: formatMessage(messages.dismissAction),
						onClick: onDismissFn,
					},
				]}
			/>
		),
		[
			formatMessage,
			onDismissFn,
			onOpenInProductHelpArticle,
			analyticsAttributes,
			messageId,
			messageType,
		],
	);
};

export default CustomNavBarColorsFlag;
